import api from './api';
import { LogSetting, ApiResponse } from '../types';

export interface LogSettingUpdate {
  enabled: boolean;
  description?: string;
}

export interface BulkLogSettingUpdate {
  log_type: string;
  log_action: string;
  enabled: boolean;
  description?: string;
}

class LogSettingsService {
  // Obter todas as configurações de logs
  async getLogSettings(): Promise<ApiResponse<LogSetting[]>> {
    const response = await api.get('/logs/settings/all');
    // O interceptor já retorna response.data, então response é na verdade {success: true, data: [...]}
    return response as unknown as ApiResponse<LogSetting[]>;
  }

  // Atualizar configuração específica
  async updateLogSetting(
    log_type: string, 
    log_action: string, 
    data: LogSettingUpdate
  ): Promise<ApiResponse<LogSetting>> {
    const response = await api.put(`/logs/settings/${log_type}/${log_action}`, data);
    return response.data;
  }

  // Atualizar múltiplas configurações
  async updateMultipleLogSettings(
    settings: BulkLogSettingUpdate[]
  ): Promise<ApiResponse<LogSetting[]>> {
    const response = await api.put('/logs/settings/bulk', { settings });
    return response.data;
  }

  // Resetar configurações para padrão
  async resetLogSettings(): Promise<ApiResponse<LogSetting[]>> {
    const response = await api.post('/logs/settings/reset');
    return response.data;
  }

  // Métodos de formatação para a interface
  formatLogType(type: string): string {
    const types = {
      'cliente': 'Cliente',
      'contrato': 'Contrato',
      'fatura': 'Fatura',
      'sistema': 'Sistema',
      'autenticacao': 'Autenticação'
    };
    return types[type as keyof typeof types] || type;
  }

  formatLogAction(action: string): string {
    const actions = {
      'criacao': 'Criação',
      'edicao': 'Edição',
      'exclusao': 'Exclusão',
      'pagamento': 'Pagamento',
      'cancelamento': 'Cancelamento',
      'autenticacao': 'Login',
      'logout': 'Logout',
      'bloqueio': 'Bloqueio',
      'ativacao': 'Ativação',
      'desativacao': 'Desativação',
      'outros': 'Outros'
    };
    return actions[action as keyof typeof actions] || action;
  }

  // Agrupar configurações por tipo para melhor organização
  groupSettingsByType(settings: LogSetting[]): Record<string, LogSetting[]> {
    return settings.reduce((groups, setting) => {
      const type = setting.log_type;
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(setting);
      return groups;
    }, {} as Record<string, LogSetting[]>);
  }

  // Obter configurações específicas de autenticação
  getAuthSettings(settings: LogSetting[]): LogSetting[] {
    return settings.filter(setting => setting.log_type === 'autenticacao');
  }

  // Obter contagem de configurações habilitadas/desabilitadas
  getSettingsStats(settings: LogSetting[]): {
    total: number;
    enabled: number;
    disabled: number;
    percentage: number;
  } {
    const total = settings.length;
    const enabled = settings.filter(s => s.enabled).length;
    const disabled = total - enabled;
    const percentage = total > 0 ? Math.round((enabled / total) * 100) : 0;

    return {
      total,
      enabled,
      disabled,
      percentage
    };
  }

  // Verificar se existem configurações importantes desabilitadas
  getImportantDisabledSettings(settings: LogSetting[]): LogSetting[] {
    const importantCombinations = [
      { log_type: 'autenticacao', log_action: 'autenticacao' },
      { log_type: 'autenticacao', log_action: 'logout' },
      { log_type: 'fatura', log_action: 'pagamento' },
      { log_type: 'contrato', log_action: 'cancelamento' }
    ];

    return settings.filter(setting => 
      !setting.enabled && 
      importantCombinations.some(important => 
        important.log_type === setting.log_type && 
        important.log_action === setting.log_action
      )
    );
  }
}

const logSettingsService = new LogSettingsService();
export default logSettingsService; 